import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import PlayerBar from './PlayerBar';
import { usePlayerStore } from '../store/playerStore';
import { useRadioStore } from '../store/radioStore';
import { useTranslation } from 'react-i18next';
import { Menu, X, Crown, User, LogOut, Heart, Tv, Users, Shield, BookOpen } from 'lucide-react';

export default function Layout() {
  const { user, isAuthenticated, logout } = useAuthStore();
  const trackCurrentTrack = usePlayerStore((state) => state.currentTrack);
  const radioCurrentTrack = useRadioStore((state) => state.currentTrack);
  const location = useLocation();
  const { i18n, t } = useTranslation();

  // Determine if player bar will be visible (same logic as PlayerBar).
  // Radio-first pages (/, /r/:slug, /r/:slug/:mood) render their own
  // StickyRadioBar via RadioHomePage — suppress the global PlayerBar
  // on those routes so the two don't stack at the bottom. Legacy
  // /radio/:slug/:mood is still here for backward-compat in case
  // anything outside the redirects mounts it.
  const isRadioMode = radioCurrentTrack !== null;
  const path = location.pathname;
  const isOnRadioPage =
    isRadioMode &&
    (path === '/' ||
      path.startsWith('/r/') ||
      path.startsWith('/radio/'));
  const hasVisiblePlayer = (trackCurrentTrack || radioCurrentTrack) && !isOnRadioPage;
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    // Use window.location for hard redirect to ensure state is cleared
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-dark-900/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/home" className="flex items-center space-x-2 group">
              <img src="/landing/logo-light.svg" alt="Fonorex" className="h-8 group-hover:scale-105 transition-transform" />
            </Link>

            <nav className="hidden md:flex space-x-1">
              <Link
                to="/profiles"
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                {t('nav.profiles')}
              </Link>
              <Link
                to="/features"
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                {t('nav.features', 'Features')}
              </Link>
              <Link
                to="/tracks"
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                {t('nav.library')}
              </Link>
              <Link
                to="/liked"
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all flex items-center gap-1.5"
              >
                <Heart size={16} />
                {t('nav.favorites')}
              </Link>
            </nav>

            <div className="flex items-center space-x-3">
              {/* Mobile Features Icon */}
              <Link
                to="/features"
                className="md:hidden p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                title={t('nav.features', 'Features')}
              >
                <BookOpen size={22} />
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              {/* Language Switcher */}
              <div className="relative hidden md:block">
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-600/80 to-secondary-600/80 hover:from-primary-500 hover:to-secondary-500 rounded-lg transition-all flex items-center space-x-2 shadow-lg"
                >
                  <span className="text-lg">🌐</span>
                  <span className="uppercase font-bold">{i18n.language}</span>
                  <span className={`text-xs transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`}>▼</span>
                </button>
                {isLangMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-dark-900 border border-white/20 rounded-lg shadow-2xl z-50 animate-scale-in">
                    {[
                      { code: 'es', name: '🇪🇸 Español', flag: '🇪🇸' },
                      { code: 'en', name: '🇬🇧 English', flag: '🇬🇧' },
                      { code: 'ru', name: '🇷🇺 Русский', flag: '🇷🇺' },
                      { code: 'lt', name: '🇱🇹 Lietuvių', flag: '🇱🇹' },
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-white/10 first:rounded-t-lg last:rounded-b-lg transition-all ${
                          i18n.language === lang.code
                            ? 'bg-gradient-to-r from-primary-600/30 to-secondary-600/30 text-white font-semibold'
                            : 'text-gray-300'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {isAuthenticated ? (
                <div className="hidden md:flex items-center space-x-3">
                  {/* Subscription status indicator */}
                  {user?.subscriptionStatus === 'active' || user?.subscriptionStatus === 'trialing' ? (
                    <Link
                      to="/profile#subscription"
                      className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded-lg transition-all"
                      title={t('nav.subscription')}
                    >
                      <Crown size={20} />
                    </Link>
                  ) : (
                    <Link
                      to="/pricing"
                      className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 rounded-lg transition-all shadow-lg hover:shadow-glow"
                    >
                      {t('nav.subscribe')}
                    </Link>
                  )}

                  {/* User Menu Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-semibold text-sm hover:opacity-90 transition-all"
                    >
                      {user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                    </button>
                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-dark-900 border border-white/20 rounded-lg shadow-2xl z-50 animate-scale-in overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/10">
                          <p className="text-sm font-medium text-white truncate">
                            {user?.username || user?.email?.split('@')[0]}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                        </div>
                        <Link
                          to="/profile"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-300 hover:bg-white/10 transition-all"
                        >
                          <User size={16} />
                          <span>{t('nav.profile')}</span>
                        </Link>
                        <Link
                          to="/features"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-300 hover:bg-white/10 transition-all"
                        >
                          <BookOpen size={16} />
                          <span>{t('nav.features', 'Features')}</span>
                        </Link>
                        <Link
                          to="/tv/remote"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-300 hover:bg-white/10 transition-all"
                        >
                          <Tv size={16} />
                          <span>{t('nav.tvRemote', 'TV Remote')}</span>
                        </Link>
                        <Link
                          to={user?.isAffiliate ? '/affiliate/dashboard' : '/affiliate/apply'}
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-300 hover:bg-white/10 transition-all"
                        >
                          <Users size={16} />
                          <span>{t('nav.affiliate', 'Affiliate Program')}</span>
                        </Link>
                        {user?.roles?.includes('admin') && (
                          <Link
                            to="/admin/affiliates"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-yellow-400 hover:bg-white/10 transition-all"
                          >
                            <Shield size={16} />
                            <span>{t('nav.admin', 'Admin Panel')}</span>
                          </Link>
                        )}
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-gray-300 hover:bg-white/10 transition-all"
                        >
                          <LogOut size={16} />
                          <span>{t('nav.logout')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="hidden md:flex items-center space-x-3">
                  <Link
                    to="/login"
                    className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                  >
                    {t('nav.signin')}
                  </Link>
                  <Link
                    to="/register"
                    className="px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 rounded-lg transition-all shadow-lg hover:shadow-glow"
                  >
                    {t('nav.signup')}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-white/10 py-4 space-y-2">
              <Link
                to="/profiles"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                {t('nav.profiles')}
              </Link>
              <Link
                to="/features"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                {t('nav.features', 'Features')}
              </Link>
              <Link
                to="/tracks"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                {t('nav.library')}
              </Link>
              <Link
                to="/liked"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                <Heart size={16} />
                {t('nav.favorites')}
              </Link>

              {/* Language Switcher Mobile */}
              <div className="px-4 py-2">
                <p className="text-xs text-gray-500 mb-2">{t('nav.language') || 'Language'}</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { code: 'es', name: '🇪🇸 ES' },
                    { code: 'en', name: '🇬🇧 EN' },
                    { code: 'ru', name: '🇷🇺 RU' },
                    { code: 'lt', name: '🇱🇹 LT' },
                  ].map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`px-3 py-2 text-sm rounded-lg transition-all ${
                        i18n.language === lang.code
                          ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-semibold'
                          : 'bg-white/5 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>

              {isAuthenticated ? (
                <>
                  {/* User info */}
                  <div className="px-4 py-3 border-t border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-semibold">
                        {user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">
                          {user?.username || user?.email?.split('@')[0]}
                        </p>
                        <p className="text-xs text-gray-400">{user?.email}</p>
                      </div>
                      {(user?.subscriptionStatus === 'active' || user?.subscriptionStatus === 'trialing') && (
                        <Crown size={16} className="text-yellow-400 ml-auto" />
                      )}
                    </div>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                  >
                    <User size={16} />
                    <span>{t('nav.profile')}</span>
                  </Link>
                  <Link
                    to="/features"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                  >
                    <BookOpen size={16} />
                    <span>{t('nav.features', 'Features')}</span>
                  </Link>
                  <Link
                    to="/tv/remote"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                  >
                    <Tv size={16} />
                    <span>{t('nav.tvRemote', 'TV Remote')}</span>
                  </Link>
                  <Link
                    to={user?.isAffiliate ? '/affiliate/dashboard' : '/affiliate/apply'}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                  >
                    <Users size={16} />
                    <span>{t('nav.affiliate', 'Affiliate Program')}</span>
                  </Link>
                  {user?.roles?.includes('admin') && (
                    <Link
                      to="/admin/affiliates"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-2 text-sm font-medium text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded-lg transition-all"
                    >
                      <Shield size={16} />
                      <span>{t('nav.admin', 'Admin Panel')}</span>
                    </Link>
                  )}
                  {user?.subscriptionStatus !== 'active' && user?.subscriptionStatus !== 'trialing' && (
                    <Link
                      to="/pricing"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block mx-4 px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 rounded-lg transition-all shadow-lg text-center"
                    >
                      {t('nav.subscribe')}
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 text-left px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                  >
                    <LogOut size={16} />
                    <span>{t('nav.logout')}</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                  >
                    {t('nav.signin')}
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block mx-4 px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 rounded-lg transition-all shadow-lg hover:shadow-glow text-center"
                  >
                    {t('nav.signup')}
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${hasVisiblePlayer ? 'pb-32' : ''}`}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className={`border-t border-white/10 mt-12 ${hasVisiblePlayer ? 'mb-24' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center space-y-4">
            <div className="flex items-center space-x-3">
              <img src="/landing/logo-light.svg" alt="Fonorex" className="h-6" />
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
              <Link to="/about" className="text-gray-400 hover:text-white transition-colors">
                {t('footer.about')}
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                {t('footer.privacy')}
              </Link>
            </div>
            <p className="text-center text-gray-400 text-sm">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>

      {/* Player Bar — skipped on radio-first routes; those render their
          own scroll-aware sticky bar via RadioHomePage/StickyRadioBar. */}
      {!isOnRadioPage && <PlayerBar />}
    </div>
  );
}
